import { ChangeEvent, FC, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from '../../../../hooks/locale'
import { usePlaceholderRotation } from './use-placeholder-rotation'
import { Caret, ErrorMessage, FieldWrapper, Ghost, TextArea } from './styles'

interface Props {
    value: string
    onChange: (value: string) => void
    hasError: boolean
    variant: 'desktop' | 'mobile'
}

const SmartSearchField: FC<Props> = ({ value, onChange, hasError, variant }) => {
    const { t } = useTranslation()
    const wrapperRef = useRef<HTMLDivElement>(null)

    // Label, then instruction, then worked examples — the order is the teaching sequence.
    const lines = [
        t('smartSearch.placeholderTitle'),
        t('smartSearch.placeholderInstruction'),
        t('smartSearch.placeholderExample'),
        t('smartSearch.placeholderExamplePetsDishwasher'),
        t('smartSearch.placeholderExampleBeach'),
        t('smartSearch.placeholderExampleInstantBooking'),
    ]

    const { visibleText, isVisible, isCaretVisible, isRunning, stop } = usePlaceholderRotation(lines, wrapperRef)
    const isRotating = isRunning && !value

    useEffect(() => {
        if (value) {
            stop()
        }
    }, [stop, value])

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLTextAreaElement>) => {
            stop()
            onChange(event.target.value)
        },
        [onChange, stop],
    )

    return (
        <FieldWrapper ref={wrapperRef}>
            <TextArea
                value={value}
                onChange={handleChange}
                onFocus={stop}
                hasError={hasError}
                isRotating={isRotating}
                variant={variant}
                placeholder={lines[0]}
                aria-label={t('smartSearch.inputLabel')}
                aria-invalid={hasError}
            />

            {isRotating && (
                <Ghost aria-hidden isVisible={isVisible} variant={variant}>
                    {visibleText}
                    {isCaretVisible && <Caret />}
                </Ghost>
            )}

            {hasError && <ErrorMessage>{t('smartSearch.noMatch')}</ErrorMessage>}
        </FieldWrapper>
    )
}

export default SmartSearchField
